import * as Backbone from "backbone";
import * as _ from "lodash";
import {CollectionFetchOptions} from "backbone";

declare var App;
var backboneFetch = Backbone.Collection.prototype.fetch;

export class BaseCollection<TModel extends Backbone.Model> extends Backbone.Collection<TModel> {

    protected timeLastFetched = 0;
    protected numFetched = 0;
    protected fetching = false;
    protected etag = null;

    constructor(models?: TModel[] | Object[], options?: any) {
        super(models, options);
    }

    fetch(options?: CollectionFetchOptions): JQueryXHR {
        this.fetching = true;
        if (options && options.reset === true) {
            this.timeLastFetched = 0;
            this.etag = null;
        }

        return backboneFetch.call(this, _.merge(options, {
            beforeSend: (jqxhr: JQueryXHR) => {
                jqxhr.setRequestHeader('if-none-match', this.etag);
            },
            success: (collection, data, options) => {
                // Success triggers before backbone's sync
                this.fetching = false;
                this.numFetched++;
                this.timeLastFetched = Math.floor((new Date()).getTime() / 1000);
                this.etag = options.xhr.getResponseHeader('ETag')
            },
            error: () => {
                // Error triggers before backbone's sync
                this.fetching = false;
            }
        }));
    }

    parse(response, options) {
        var models;
        if (options.xhr.status === 304) {
            models = this.models;
        } else {
            models = response;
        }
        return super.parse(models, options);
    }

    save(options: JQueryAjaxSettings) {
        Backbone.sync("create", new this.model(), options);
    }

    timesFetched() {
        return this.numFetched
    }

    isFetching() {
        return this.fetching;
    }

    lastFetched() {
        return this.timeLastFetched;
    }

    resetFetched() {
        this.numFetched = 0;
        this.timeLastFetched = 0;
    }

    getCollectionFetchPromise(options?: Backbone.CollectionFetchOptions) {
        if (!this.isFetching()) return this.fetch(options);

        var d = $.Deferred();
        this.once('sync', () => {
            d.resolve();
        });
        return d;
    }

    getETag() {
        return this.etag;
    }
}


export class CachingCollection<TModel extends Backbone.Model> extends BaseCollection<TModel> {

    protected _storageKey: string = null;
    protected _etagKey: string = null;
    protected _versionKey: string = null;
    protected _modelsKey: string = null;

    constructor(models?: TModel[] | Object[], options?: any) {
        super(models, options);

        var opts = options ? JSON.stringify(options) : 'null';

        this._storageKey = 'collection.' + this.constructor['name'] + '.' + opts;
        this._etagKey = this._storageKey + '.etag';
        this._versionKey = this._storageKey + '.version';
        this._modelsKey = this._storageKey + '.models';

        if (!models || (models && !models.length)) {
            this._retrieveModels();
        }
    }

    fetch(options?: CollectionFetchOptions): JQueryXHR {
        var xhr = super.fetch(options);
        $.when(xhr).done(() => {
            this._storeModels();
        });
        return xhr;
    }

    protected getStorage () {
        return sessionStorage;
    }

    protected _storeModels() {
        var currentEtag = this.getStorage().getItem(this._etagKey);
        var currentVersion = this.getStorage().getItem(this._versionKey);

        if (currentEtag !== this.etag || currentVersion !== App.Version) {
            this.getStorage().setItem(this._etagKey, this.etag);
            this.getStorage().setItem(this._versionKey, App.Version);
            this.getStorage().setItem(this._modelsKey, JSON.stringify(this.toJSON()));
        } else if(!this.models.length) {
            this.etag = null;
        }
    }

    protected _retrieveModels() {
        var version = this.getStorage().getItem(this._versionKey);
        if (version && version === App.VERSION) {
            var etag = this.getStorage().getItem(this._etagKey);
            var models = this.getStorage().getItem(this._modelsKey);
            try {
                var parsed = JSON.parse(models);
                this.set(parsed.models);
                this.etag = etag;
            } catch (e) {
                this.reset();
                this.etag = null;
            }
        } else {
            this.getStorage().removeItem(this._modelsKey);
            this.getStorage().removeItem(this._versionKey);
            this.getStorage().removeItem(this._etagKey);
            this.reset();
            this.etag = null;
        }
    }
}


export class InsecureCachingCollection<TModel extends Backbone.Model> extends CachingCollection<TModel> {

    protected getStorage() {
        return localStorage
    }

}


// WEBPACK FOOTER //
// ./srv/public/js/collections/_BaseCollection.ts